import { CSSProperties } from 'react';
import { useQuery } from '@apollo/client';
import { Stats } from './components/Meta';
import { MediaPostListItem } from '../../types';
import { Feature } from './components/Feature';
import { PostBodyType, PostStatus } from '../../lib/graphql.document';
import { ROUTES } from '../../lib/ROUTES';
import { Clickable } from '../Clickable';
import { tw } from '../../styles/tw';
import { HIDDEN_PAID_POSTS } from '../../lib/HIDDEN_PAID_POSTS';
import { useReportPostViewForRanking } from '../../react-hooks/useReportPostViewForRanking';
import { PostScores } from '../MediaStats';
import { MediaPostBodyDocument } from './MediaPostBody.document';

interface MediaCardProps {
  article: MediaPostListItem;
  isPromoted?: boolean;
  isImagePrioritized?: boolean;
  className?: string;
  style?: CSSProperties;
}

const MediaCard = ({ article, isPromoted, isImagePrioritized, className, style }: MediaCardProps): JSX.Element => {
  const stats: Stats = {
    isPublished: article.status === PostStatus.Published,
    commentsCount: article.commentsCount,
  };

  const { data: postBodyData } = useQuery(MediaPostBodyDocument, {
    variables: {
      slug: article.slug,
    },
    skip: !article.slug || article.bodyType !== PostBodyType.Link,
  });

  const reportPostViewForRanking = useReportPostViewForRanking();

  const scores: PostScores | undefined =
    article.totalScore !== null
      ? {
          totalScore: article.totalScore ?? 0,
          internalViewsScore: article.internalViewsScore ?? 0,
          internalViewsCount: article.internalViewsCount ?? 0,
          publishDateScore: article.publishDateScore ?? 0,
        }
      : undefined;

  const url =
    article.bodyType === PostBodyType.Link
      ? (postBodyData?.mediaPost?.body ?? '')
      : ROUTES.POST.$buildPath({ params: { slug: article.slug } });

  return (
    <div style={style} className={tw('flex w-full flex-col', className)}>
      <Clickable
        className="[align-items:normal] [justify-content:normal] [&>section]:w-full [&_*]:transition-colors [&_*]:duration-75 [&_*]:ease-in-out [@media(hover:hover){&:hover_h2}]:text-text-secondary"
        to={url}
        onClick={() => {
          reportPostViewForRanking(article.slug);
        }}
      >
        <Feature
          isLarge={Boolean(isPromoted)}
          isImagePrioritized={isImagePrioritized}
          species={article.species}
          stats={stats}
          title={article.title}
          subtitle={article.subtitle}
          cover={article.cover}
          isPaid={article.isSpecialProject && !HIDDEN_PAID_POSTS.includes(article.id)}
          status={article.status}
          scores={scores}
        />
      </Clickable>
    </div>
  );
};

export { MediaCard };
export type { MediaCardProps };
